import HeaderBar from "../components/layout/Headerbar";
import NavBar from "../components/layout/NavBar";
import { useApiCustomPois, apiDeleteCustomPoi, apiPublishCustomPoi } from "../api/customPoi";
// CustomPoiModel is not used in this component
import { useNavigate } from "react-router-dom";
import LargeButton from "../components/input/LargeButton";
import SmallButton from "../components/input/SmallButton";
import { useState } from "react";
import addIcon from "../assets/add.png";
import locationIcon from "../assets/location.png";
import "./CustomPoiList.css";

function CustomPoiList() {
  const customPois = useApiCustomPois();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const isUserForbidden =
    customPois.error?.response?.status === 403 ? true : false;

  const filteredPois = customPois.data?.filter((poi) => {
    if (filterStatus === "all") return true;
    return poi.status === filterStatus;
  }) || [];

  const handleDelete = async (id: number) => {
    const isConfirmed = window.confirm(
      "***WARNING***\n\nYou are about to delete a Custom POI. This action cannot be undone. Are you sure you want to proceed?"
    );
    if (!isConfirmed) return;

    try {
      await apiDeleteCustomPoi(id);
      customPois.refresh();
    } catch (error) {
      console.error("Failed to delete Custom POI:", error);
    }
  };

  const handlePublish = async (id: number) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to publish this Custom POI? It will become visible to users."
    );
    if (!isConfirmed) return;

    try {
      await apiPublishCustomPoi(id);
      customPois.refresh();
    } catch (error) {
      console.error("Failed to publish Custom POI:", error);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "published":
        return "status-published";
      case "draft":
        return "status-draft";
      case "archived":
        return "status-archived";
      default:
        return "";
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "N/A";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="app">
      <HeaderBar>
        <img
          src={locationIcon}
          alt="Custom POIs"
          height={32}
          style={{ display: "inline-block", marginLeft: 20, marginTop: 25 }}
        />
        <h1
          style={{
            display: "inline-block",
            marginLeft: 8,
            verticalAlign: "top",
            paddingTop: 13.5,
            fontWeight: 600,
          }}
        >
          Custom POIs
        </h1>
        {!isUserForbidden && (
          <div
            style={{
              display: "inline-flex",
              right: 0,
              position: "absolute",
              boxSizing: "border-box",
              padding: 15,
            }}
          >
            <LargeButton
              text="Add new POI"
              onClick={() => {
                navigate("/custom-pois/new");
              }}
              icon={addIcon}
              color={"#F0EDE0"}
            />
          </div>
        )}
      </HeaderBar>

      <NavBar selectedIndex={3} isUserForbidden={isUserForbidden} />

      <div className="content" style={{ paddingLeft: 16, paddingRight: 16 }}>
        {!isUserForbidden ? (
          <div>
            <p
              style={{
                marginLeft: 4,
                paddingBottom: 28,
                maxWidth: 800,
                fontSize: 18,
              }}
            >
              Create and manage custom Points of Interest tied to specific personas.
              These POIs will appear in unified search results alongside Google Places.
            </p>

            {/* Status Filter */}
            <div className="filter-section">
              <label htmlFor="status-filter">Filter by status:</label>
              <select
                id="status-filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="status-filter"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
              <span className="poi-count">
                Showing {filteredPois.length} POI{filteredPois.length !== 1 ? 's' : ''}
              </span>
            </div>

            <table className="poi-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Persona</th>
                  <th>Location</th>
                  <th>Duration</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPois.map((poi) => (
                  <tr key={poi.id}>
                    <td>
                      <div className="poi-title-cell">
                        <strong>{poi.title}</strong>
                        <div className="poi-description-preview">
                          {poi.description.length > 100
                            ? `${poi.description.substring(0, 100)}...`
                            : poi.description}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="persona-cell">
                        {poi.persona && (
                          <>
                            <img
                              src={poi.persona.pictureUrl}
                              alt={poi.persona.name}
                              className="persona-avatar"
                            />
                            <span>{poi.persona.name}</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="location-cell">
                        {Number(poi.latitude).toFixed(4)}, {Number(poi.longitude).toFixed(4)}
                      </div>
                    </td>
                    <td>{formatDuration(poi.audioLength)}</td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(poi.status)}`}>
                        {poi.status}
                      </span>
                    </td>
                    <td>
                      {new Date(poi.createdAt).toLocaleDateString()}
                      {poi.publishedAt && (
                        <div className="published-date">
                          Published: {new Date(poi.publishedAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <SmallButton
                          text="Edit"
                          icon={null}
                          isOutlineOnly={true}
                          onClick={() => navigate(`/custom-pois/${poi.id}`)}
                          className="poi-btn"
                        />
                        {poi.status === 'draft' && (
                          <SmallButton
                            text="Publish"
                            icon={null}
                            isOutlineOnly={false}
                            onClick={() => handlePublish(poi.id)}
                            className="poi-btn publish-btn"
                          />
                        )}
                        <SmallButton
                          text="Delete"
                          icon={null}
                          isOutlineOnly={true}
                          onClick={() => handleDelete(poi.id)}
                          className="poi-btn delete-btn"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredPois.length === 0 && (
              <div className="empty-state">
                <p>No Custom POIs found.</p>
                <LargeButton
                  text="Create your first POI"
                  onClick={() => navigate("/custom-pois/new")}
                  icon={addIcon}
                  color={"#F0EDE0"}
                />
              </div>
            )}
          </div>
        ) : (
          <div
            className="grid-item"
            style={{
              height: "auto",
              margin: "auto",
            }}
          >
            <div className="host-image">
              <img
                src="https://d1m9alagf2gv12.cloudfront.net/persona/placeholder.jpg"
                alt="access denied"
                height={"100%"}
                width={"100%"}
              />
            </div>
            <h1
              title="Insufficient Permissions"
              style={{
                maxLines: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Insufficient Permissions
            </h1>
            <p>Please contact your administrator for access.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomPoiList;
